import { useState } from 'react';
import { APIDocumentCategory, APIDocumentSession, DocumentType, IDocumentCategory } from '../types/documents';
import {
  adminAcceptCompanyDocumentCategory,
  adminCreateCompanyDocumentCategory,
  adminGetCompanyDocumentCategories,
  adminRejectCompanyDocumentCategory,
  adminToggleCompanyDocumentCategory,
  deleteDocumentAsset,
  downloadDocumentAsset,
  downloadDocumentAssetsByDocumentType,
  getCompanyDocumentCategories,
  uploadDocumentAssets,
  adminNotifyUserCompanyDocument,
  downloadDocumentAssetsByDocumentId,
} from '../services/documents';
import { logEvent } from '@amplitude/analytics-browser';
import { docsCollectionEvents } from '../constants/events';
import {
  formatAPIAsset,
  getStatusFromDocuments,
  getStatusFromSession,
  groupDocumentsbyGroup,
  groupDocumentsbyDocumentType,
} from '../utils/docs';
import { contentTypeToExtensionMap } from '../utils/common';

export function formatAPICategory(
  {
    id,
    group,
    nameEn,
    nameEs,
    descriptionEn,
    descriptionEs,
    documents,
    status,
    enabled,
    isExtraDocument,
    templateEn,
    templateEs,
    documentType,
  }: APIDocumentCategory,
  language: string
): IDocumentCategory {
  /* Transforms an API Document Category into our internal representation */
  const template = language === 'es' ? templateEs : templateEn;

  return {
    id,
    group,
    name: language === 'es' ? nameEs : nameEn,
    description: language === 'es' ? descriptionEs : descriptionEn,
    assets: documents[0].lastSession.assets.map((apiAsset) => ({
      ...formatAPIAsset(apiAsset),
    })),
    isLoading: false,
    rejectReason: documents[0].prevSessionRejectedComment,
    status: getStatusFromDocuments(documents, status),
    documentId: documents[0].id,
    enabled,
    isExtraDocument,
    groupName: group,
    // use name in english to uniquely identify documents independently from user language (analytics purposes)
    keyName: nameEn,
    template: !!template ? formatAPIAsset(template) : null,
    documentType: documentType,
    notificationDate: documents?.[0]?.lastSession?.notificationDate,
  };
}

export function useDocuments(companyId: number | undefined, language: string, isAdmin = false) {
  const [documentCategories, setDocumentCategories] = useState<IDocumentCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  function setCategoryIsLoading(categoryId: number, value: boolean) {
    setDocumentCategories((prev) =>
      prev.map((category) => (category.id === categoryId ? { ...category, isLoading: value } : category))
    );
  }

  function setAssetIsLoading(categoryId: number, assetId: number, value: boolean) {
    setDocumentCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              assets: category.assets.map((asset) =>
                asset.id === assetId ? { ...asset, isLoading: value } : asset
              ),
            }
          : category
      )
    );
  }

  function notifyUserCompanyDocument(documentId: number) {
    /* Notifies a user about a document */
    if (!companyId) return;

    adminNotifyUserCompanyDocument(companyId, documentId).then(() => {
      setDocumentCategories((prev) =>
        prev.map((category) =>
          category.documentId === documentId
            ? {
                ...category,
                notificationDate: new Date().toISOString(),
              }
            : category
        )
      );
    });
  }

  function loadCompanyCategories() {
    /* Loads all Document categories from the API */
    if (!companyId) return;

    const _getCompanyDocumentCategories = isAdmin
      ? adminGetCompanyDocumentCategories
      : getCompanyDocumentCategories;

    setIsLoading(true);
    _getCompanyDocumentCategories(companyId)
      .then((categories) => {
        setDocumentCategories(
          categories
            .filter(({ enabled, multiple }) => (isAdmin ? true : enabled && !multiple))
            .sort((a, b) => a.nameEn.localeCompare(b.nameEn))
            .map((apiCategory) => formatAPICategory(apiCategory, language))
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function updateCategory(categoryId: number, sessionResult: APIDocumentSession) {
    /* Updates an internal document category based on an API result */
    setDocumentCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              assets: sessionResult.assets.map((asset) => formatAPIAsset(asset)),
              status: getStatusFromSession(sessionResult),
              rejectReason: sessionResult.rejectedComment,
            }
          : category
      )
    );
  }

  function updateCategoryToggle(categoryId: number) {
    /* Updates category after toggle */
    setDocumentCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              enabled: !category.enabled,
            }
          : category
      )
    );
  }

  function toggleCategory(categoryId: number) {
    /* Enables/Disables a Category */
    if (!companyId || !isAdmin) return;

    setCategoryIsLoading(categoryId, true);
    adminToggleCompanyDocumentCategory(companyId, categoryId).then(() => {
      updateCategoryToggle(categoryId);
      setCategoryIsLoading(categoryId, false);
    });
  }

  function approveCategory(categoryId: number) {
    /* Accepts a Category */
    if (!companyId || !isAdmin) return;

    setCategoryIsLoading(categoryId, true);
    adminAcceptCompanyDocumentCategory(companyId, categoryId).then((result) => {
      updateCategory(categoryId, result.documents[0].lastSession);
      setCategoryIsLoading(categoryId, false);
    });
  }

  function rejectCategory(categoryId: number, rejectionComment: string) {
    /* Rejects a Category */
    if (!companyId || !isAdmin) return;

    setCategoryIsLoading(categoryId, true);
    adminRejectCompanyDocumentCategory(companyId, categoryId, rejectionComment).then((result) => {
      updateCategory(categoryId, {
        ...result.documents[0].lastSession,
        rejectedComment: result.documents[0].prevSessionRejectedComment,
      });
      setCategoryIsLoading(categoryId, false);
    });
  }

  function createCategory(values: {
    group: string;
    nameEn: string;
    nameEs: string;
    descriptionEn: string;
    descriptionEs: string;
    documentType: DocumentType;
  }) {
    if (!companyId || !isAdmin) return;
    return adminCreateCompanyDocumentCategory(companyId, values).then(() => {
      return loadCompanyCategories();
    });
  }

  function deleteAsset(categoryId: number, documentId: number, assetId: number) {
    /* Deletes an asset using the API */
    if (!companyId) return;

    setAssetIsLoading(categoryId, assetId, true);
    deleteDocumentAsset(companyId, documentId, assetId, isAdmin)
      .then((result) => {
        updateCategory(categoryId, result);
        logEvent(docsCollectionEvents.DELETE_DOC, { Name: '' });
      })
      .finally(() => {
        setAssetIsLoading(categoryId, assetId, false);
      });
  }

  function downloadAsset(
    categoryId: number,
    documentId: number,
    assetId: number,
    documentName: string
  ) {
    /* Downloads an asset using the API */
    if (!companyId) return;

    setAssetIsLoading(categoryId, assetId, true);
    downloadDocumentAsset(companyId, documentId, assetId, isAdmin)
      .then((response) => {
        const contentType = response.headers['content-type'];
        const blob = response.data;

        const extension = contentTypeToExtensionMap[contentType] || '.bin';

        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${documentName}${extension}`;
        document.body.appendChild(a);

        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        logEvent(docsCollectionEvents.DOWNLOAD_DOC, { Name: assetId.toString() });
      })
      .finally(() => {
        setAssetIsLoading(categoryId, assetId, false);
      });
  }

  function downloadAllAssets(zipName: string, documentType: DocumentType) {
    /* Downloads all assets using the API */
    if (!companyId) return;

    downloadDocumentAssetsByDocumentType(companyId, documentType, isAdmin)
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        // Create a link element to trigger the download
        const a = document.createElement('a');
        a.href = url;
        a.download = `${zipName}.zip`;
        document.body.appendChild(a);
        // Trigger the download and clean up
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        logEvent(docsCollectionEvents.DOWNLOAD_ALL_DOCS, { Name: zipName });
      })
      .catch((error) => {
        console.error('Error downloading all assets:', error);
      });
  }

  function downloadAssetsByDocumentId(zipName: string, documentId: number) {
    /* Downloads all assets using the API */
    if (!companyId) return;

    downloadDocumentAssetsByDocumentId(companyId, documentId)
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        // Create a link element to trigger the download
        const a = document.createElement('a');
        a.href = url;
        a.download = `${zipName}.zip`;
        document.body.appendChild(a);
        // Trigger the download and clean up
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        logEvent(docsCollectionEvents.DOWNLOAD_BY_CATEGORY, { Name: zipName });
      })
      .catch((error) => {
        console.error('Error downloading assets:', error);
      });
  }

  async function uploadAssets(
    categoryId: number,
    documentId: number,
    files: File[],
    categoryName: string,
    groupName: string,
    formDatas?: FormData[]
  ): Promise<void> {
    /* Uploads assets using the API */
    if (!companyId) return;

    let formDataValues = formDatas;
    if (!formDataValues?.length && !!files?.length) {
      formDataValues = [];
      files.forEach((file) => {
        const formData = new FormData();
        formData.append('asset', file, file.name);
        formData.append('file_name', file.name);
        formData.append('asset_type', 'document');
        formDataValues!.push(formData);
      });
    }

    setCategoryIsLoading(categoryId, true);

    const uploadPromises = formDataValues!.map((formData) =>
      uploadDocumentAssets(companyId, documentId, formData, isAdmin).then((result) => {
        logEvent(docsCollectionEvents.UPLOAD_DOC, {
          File: formData.get('file_name'),
          'Document Group': groupName,
          'Document Name': categoryName,
        });
        updateCategory(categoryId, result);
      })
    );

    await Promise.all(uploadPromises);
    setCategoryIsLoading(categoryId, false);
  }

  const categoriesGroupedByDocumentType = groupDocumentsbyDocumentType(documentCategories);

  const groupedCategoriesByDocType = Object.keys(categoriesGroupedByDocumentType).reduce((acc, docType) => {
    acc[docType] = {
      groupedCategories: groupDocumentsbyGroup(
        // separate extra documents for admin purposes
        categoriesGroupedByDocumentType[docType].filter((category) =>
          isAdmin ? !category.isExtraDocument : true
        )
      ),
      groupedExtraCategories: groupDocumentsbyGroup(
        categoriesGroupedByDocumentType[docType].filter((category) => category.isExtraDocument)
      ),
    };
    return acc;
  }, {} as Record<string, { groupedCategories: Record<string, IDocumentCategory[]>; groupedExtraCategories: Record<string, IDocumentCategory[]> }>);

  return {
    groupedCategoriesByDocType, // for each document type, groups categories by group and discriminate admin created ones.
    isLoading,
    loadCompanyCategories,
    uploadAssets,
    deleteAsset,
    toggleCategory,
    approveCategory,
    rejectCategory,
    createCategory,
    downloadAsset,
    downloadAllAssets,
    notifyUserCompanyDocument,
    downloadAssetsByDocumentId,
  };
}
